/**
 * When the plan starts — a month grid with a dot under every day the program trains on.
 *
 * ── Hand-built, and a picker rather than a date input ────────────────────────────────────────
 * `CLAUDE.md`: no third-party calendar. And not `<input type="date">` either, because the whole
 * point of this control is the DOTS: a trainer choosing a Wednesday can see, before they press,
 * that Monday's session is the one that will not happen. A native picker shows a plain month and
 * the consequence arrives afterwards, as a sentence about something they can no longer see.
 *
 * ── One tab stop, arrows inside it ───────────────────────────────────────────────────────────
 * `shared/lib/useGridFocus`, on its third consumer — the progression matrix and `/raporty`'s
 * client × survey grid are the other two. 42 cells each carrying a tab stop is 42 presses to reach
 * the footer.
 *
 * ── The grid is six rows on every month ──────────────────────────────────────────────────────
 * `monthGrid` draws the ceiling rather than what the month needs, so paging never changes the
 * dialog's height. A picker that grew a row in February would move the whole form under the cursor.
 *
 * ── TWO windows draw it, and that is why the name and the bounds are parameters ─────────────
 * The form asks for the plan's start date; `OccurrenceEditPopover` asks for one session's new one,
 * over its own window (the assignable range, floored at the plan's start). One date pattern per
 * window is the point — a native `<input type="date">` in the popover meant the trainer met the
 * browser's own calendar inside a dialog whose other date field is this one.
 */

import { useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useGridFocus } from '~/shared/lib/useGridFocus';
import { parseIsoDate } from '~/shared/lib/calendarDate';
import { dateTimeFormat } from '~/shared/lib/intlCache';
import { capitaliseFirst } from '~/shared/lib/localeCase';

import { MONTH_ROWS, firstOfMonth, monthGrid, shiftMonth } from '../model/monthGrid';
import type { DateWindow } from '../model/assignRequest';

import { ChevronLeftGlyph, ChevronRightGlyph } from './AssignGlyphs';
import styles from './AssignPlanDialog.module.css';

interface StartMonthPickerProps {
  /** The chosen date, or `null` — variant D opens on no date at all. */
  readonly value: string | null;
  readonly window: DateWindow;
  readonly programmedWeekdays: readonly number[];
  readonly onChange: (iso: string) => void;
  readonly locked: boolean;
  readonly reasonId: string | null;
  /**
   * The help sentence under the field — what this very grid's next press strikes out.
   *
   * Named here rather than left as loose text beside the grid: a sentence a control does not
   * reference is a sentence only a sighted reader gets, and this one is the whole reason variant D
   * draws a picker instead of a date input.
   */
  readonly helpId: string | null;
  /**
   * The grid's own accessible name, stated by the caller.
   *
   * Two windows draw this picker and they are choosing different things — the plan's start date on
   * the form, one session's new date in the popover — so the name cannot be baked in here. A grid
   * announcing "Kalendarz wyboru daty startu" over a single session's move is a name that lies.
   */
  readonly gridLabel: string;
  readonly locale: string;
}

export function StartMonthPicker({
  value,
  window,
  programmedWeekdays,
  onChange,
  locked,
  reasonId,
  helpId,
  gridLabel,
  locale,
}: StartMonthPickerProps) {
  const { t } = useTranslation();
  const grid = useRef<HTMLDivElement>(null);
  const gridId = useId();
  // Opens on the month the chosen date is in, or on the first month the trainer may choose from.
  const [month, setMonth] = useState(() =>
    firstOfMonth(parseIsoDate(value) ?? window.earliest),
  );

  const drawn = monthGrid(month, window, programmedWeekdays);
  const navigation = useGridFocus({ gridRef: grid, rowCount: MONTH_ROWS, columnCount: 7 });

  const monthName = dateTimeFormat(locale, { month: 'long', year: 'numeric' });
  const dayName = dateTimeFormat(locale, { weekday: 'long', day: 'numeric', month: 'long' });
  const weekdayShort = dateTimeFormat(locale, { weekday: 'short' });

  return (
    <div className={styles.picker}>
      <div className={styles.pickerHead}>
        <span className={styles.pickerMonth}>{capitaliseFirst(monthName.format(drawn.month), locale)}</span>
        <span className={styles.pickerNav}>
          <button
            type="button"
            className={styles.pickerStep}
            aria-label={t('web:assign.start.previousMonth')}
            onClick={() => {
              setMonth((current) => shiftMonth(current, -1));
            }}
          >
            <ChevronLeftGlyph />
          </button>
          <button
            type="button"
            className={styles.pickerStep}
            aria-label={t('web:assign.start.nextMonth')}
            onClick={() => {
              setMonth((current) => shiftMonth(current, 1));
            }}
          >
            <ChevronRightGlyph />
          </button>
        </span>
      </div>

      <div
        ref={grid}
        id={gridId}
        className={styles.pickerGrid}
        role="grid"
        aria-label={gridLabel}
        aria-describedby={[reasonId, helpId].filter(Boolean).join(' ') || undefined}
        onKeyDown={navigation.onKeyDown}
      >
        <div className={styles.pickerWeekdays} role="row">
          {/* 4 January 2027 is a Monday, so `4 + column` walks Monday to Sunday. */}
          {Array.from({ length: 7 }, (_, column) => (
            <span key={column} className={styles.pickerWeekday} role="columnheader">
              {weekdayShort.format(new Date(2027, 0, 4 + column))}
            </span>
          ))}
        </div>

        {drawn.rows.map((row, rowIndex) => (
          <div key={row[0]?.iso ?? rowIndex} className={styles.pickerRow} role="row">
            {row.map((day, column) => {
              const focused = navigation.focus.row === rowIndex && navigation.focus.column === column;
              const chosen = day.iso === value;
              return (
                <button
                  key={day.iso}
                  type="button"
                  role="gridcell"
                  data-cell={`${String(rowIndex)}-${String(column)}`}
                  tabIndex={focused ? 0 : -1}
                  className={[
                    styles.pickerDay,
                    day.inMonth ? '' : styles.pickerDayOutside,
                    chosen ? styles.pickerDayChosen : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  // `aria-disabled`, not `disabled`: a day outside the window keeps its place in
                  // the grid's walk, so the arrows do not skip a hole the trainer cannot see.
                  aria-disabled={day.selectable && !locked ? undefined : 'true'}
                  aria-selected={chosen}
                  aria-label={dayName.format(parseIsoDate(day.iso) ?? new Date())}
                  onFocus={() => {
                    navigation.onCellFocus(rowIndex, column);
                  }}
                  onClick={() => {
                    if (day.selectable && !locked) onChange(day.iso);
                  }}
                >
                  <span className={styles.pickerNumber}>{day.dayOfMonth}</span>
                  {day.trains && <span className={styles.pickerDot} aria-hidden="true" />}
                </button>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}
